"use client";
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { IoArrowBack } from "react-icons/io5";
import { FiChevronRight, FiX } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import ClientStatsCard from "./ClientStatsCard";
import ClientInfoCard from "./ClientInfoCard";
import ReviewsList from "./ReviewsList";
import { REST_API_URL } from "../../../api/config";

const API_BASE = REST_API_URL;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

// ---------------- NotesSection ----------------
function NotesSection({ title, text }) {
  const [showModal, setShowModal] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const maxLength = 200;
  
  const truncateText = (text, maxLength) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const displayText = truncateText(text, maxLength);
  const isLongText = text && text.length > maxLength;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 25 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="flex flex-col gap-2"
      >
        <h4 className="font-semibold text-[#302B2B] text-[16px] flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-[#5563F6]"></div>
          {title}
        </h4>
        <div className="bg-white rounded-2xl px-6 py-4 text-[14px] leading-relaxed text-[#7A7A7A] shadow-sm border border-gray-100">
          {displayText}
          {isLongText && (
            <motion.button
              onClick={() => setShowModal(true)}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              className="ml-3 inline-flex items-center gap-1.5 text-[#5563F6] hover:text-[#3f4ee5] font-medium text-[13px] transition-all group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>Read more</span>
              <motion.div
                animate={{ x: isHovered ? 4 : 0 }}
                transition={{ type: "spring", stiffness: 400, damping: 25 }}
              >
                <FiChevronRight className="text-[14px] group-hover:translate-x-0.5 transition-transform" />
              </motion.div>
            </motion.button>
          )}
        </div>
      </motion.div>

      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.85, opacity: 0, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className="bg-white rounded-2xl max-w-3xl w-full p-8 max-h-[85vh] overflow-hidden shadow-2xl border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-1 h-8 rounded-full bg-gradient-to-b from-[#5563F6] to-[#8A94FF]"></div>
                  <h3 className="text-2xl font-normal text-[#302B2B]">{title}</h3>
                </div>
                <motion.button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-gray-700"
                  whileHover={{ rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FiX className="text-2xl" />
                </motion.button>
              </div>
              
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-[#7A7A7A] leading-relaxed whitespace-pre-wrap text-[15px] max-h-[60vh] overflow-y-auto pr-2 scrollbar-thin"
              >
                {text || "—"}
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ---------------- Loader ----------------
function Loader() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-[#F6F8FF] to-[#EFF2FF]"
    >
      <motion.div
        animate={{ rotate: 360, scale: [1, 1.1, 1] }}
        transition={{
          rotate: { repeat: Infinity, duration: 1.5, ease: "linear" },
          scale: { repeat: Infinity, duration: 1.5, ease: "easeInOut" },
        }}
        className="w-16 h-16 border-4 border-[#5563F6] border-t-transparent rounded-full shadow-lg"
      />
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 1, repeat: Infinity, repeatType: "mirror" }}
        className="text-[#5563F6] font-medium text-[15px] mt-5 tracking-wide bg-gradient-to-r from-[#5563F6] to-[#8A94FF] bg-clip-text text-transparent"
      >
        Loading client data...
      </motion.span>
    </motion.div>
  );
}

// ---------------- ClientDetails ----------------
export default function ClientDetails({ id, onBack }) {
  const [client, setClient] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [reviews, setReviews] = useState([]);

  const token = localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken");

  const fetchAll = useCallback(async () => {
    try {
      const headers = buildHeaders(token);
      const [info, stats, revs] = await Promise.all([
        axios.get(`${API_BASE}/client/client-view/${id}/`, { headers }),
        axios.get(`${API_BASE}/client/client-view/${id}/header-analytics/`, { headers }),
        axios.get(`${API_BASE}/client/client-view/${id}/reviews/`, { headers }),
      ]);
      setClient(info.data);
      setAnalytics(stats.data);
      setReviews(revs.data || []);
    } catch (err) {
      console.error("❌ Failed to load client:", err);
    }
  }, [id, token]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (!client || !analytics) return <Loader />;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="bg-gradient-to-br from-[#F6F8FF] to-[#EFF2FF] rounded-2xl p-5"
    >
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="flex items-center gap-3 mb-8"
      >
        <motion.button
          whileHover={{ scale: 1.1, x: -3 }}
          whileTap={{ scale: 0.95 }}
          onClick={onBack}
          className="p-2.5 hover:bg-white/50 rounded-xl transition-all duration-200 shadow-sm border border-gray-100 bg-white"
        >
          <IoArrowBack className="text-gray-700 w-5 h-5" />
        </motion.button>
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-gradient-to-b from-[#5563F6] to-[#8A94FF]"></div>
          <div className="flex flex-col">
            <span className="text-xs text-gray-400 font-medium">Client Details</span>
            <h2 className="text-2xl font-normal text-[#302B2B] leading-tight">
              {client?.name
                ? client.name
                : client?.instagram_username
                  ? `@${client.instagram_username.replace(/^@/, "")}`
                  : client?.whatapp_number
                    ? `+${client.whatapp_number.replace(/^\+/, "")}`
                    : ""}
            </h2>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
        className="grid grid-cols-1 lg:grid-cols-3 gap-6"
      >
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <ClientStatsCard client={client} analytics={analytics} onClientUpdated={fetchAll} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 25 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          className="col-span-2 flex flex-col gap-6"
        >
          <ClientInfoCard client={client} />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
            className="flex flex-col bg-gradient-to-br from-white to-gray-50 rounded-2xl gap-6 p-6 shadow-sm border border-gray-100"
          >
            <NotesSection title="About Service Notes" text={client.about_service_notes} />
            <NotesSection title="Other notes" text={client.other_notes} />
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.4, ease: "easeOut" }}
        className="mt-8"
      >
        <ReviewsList reviews={reviews} client={client} />
      </motion.div>
    </motion.div>
  );
}